import React, { useState } from "react";
import { useWidgetMsg } from "../../hooks/widget/useWidgetMsg";
import { normalizeTraceSummary, toStatusKey, toStep } from "../../streaming/reduceTrace";

/**
 * What the agent is doing, told to the customer (spec-langchain-native-runtime 08c; canvas
 * `langchain-native-runtime/Widget`). Three states:
 *  - OFF       no live trace and no summary → renders nothing (Chat keeps the typing dots until the
 *              first chunk — today's behaviour);
 *  - LIVE      a neutral status line and the step LABELS, announced politely (`role="status"`);
 *  - COLLAPSED one line "N steps"; a toggle button expands the labels when this session saw them
 *              (after a reload only the count is known — the line is not interactive then).
 *
 * Only labels and statuses reach this component (the model copies whitelisted fields); it renders
 * nothing it is not given — never a tool name, an argument, a result or reasoning text (D-L17).
 */

const pluralCategory = (lang, count) => {
	try {
		return new Intl.PluralRules(lang).select(count);
	} catch (_) {
		return count === 1 ? "one" : "other";
	}
};

const BoltIcon = () => (
	<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true" focusable="false">
		<path d="M13 2L4 14h7l-1 8 9-12h-7z" />
	</svg>
);

const Chevron = ({ open }) => (
	<svg className={`trace-chevron${open ? " trace-chevron--open" : ""}`} width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true" focusable="false">
		<path d="M6 9l6 6 6-6" />
	</svg>
);

const STEP_MARK = { done: "✓", failed: "✕", running: "" };

function StepList({ steps, msg }) {
	return (
		<ul className="trace-steps">
			{steps.map((s) => (
				<li key={s.id} className={`trace-step trace-step--${s.status}`} data-status={s.status}>
					<span className="trace-step-mark" aria-hidden="true">{STEP_MARK[s.status]}</span>
					<span className="trace-step-label">{s.label}</span>
					{s.status === "failed" && <span className="trace-sr-only">{` — ${msg("trace.failed")}`}</span>}
				</li>
			))}
		</ul>
	);
}

export default function TraceBlock({ trace, summary, steps, streaming }) {
	const { msg, lang } = useWidgetMsg();
	const [open, setOpen] = useState(false);

	const live = streaming === true && trace && trace.off === false;
	if (live) {
		const liveSteps = (Array.isArray(trace.steps) ? trace.steps : []).map(toStep).filter(Boolean);
		const running = [...liveSteps].reverse().find((s) => s.status === "running");
		const head = running
			? `${running.label}…`
			: msg(`trace.${toStatusKey(trace.status)}`);
		return (
			<div className="trace-block trace-block--live" role="status" aria-live="polite">
				<div className="trace-head">
					<span className="trace-icon"><BoltIcon /></span>
					<span className="trace-status">{head}</span>
				</div>
				{liveSteps.length > 0 && (
					<div className="trace-body">
						<StepList steps={liveSteps} msg={msg} />
					</div>
				)}
			</div>
		);
	}

	const collapsed = normalizeTraceSummary(summary);
	if (!collapsed) return null;
	const count = collapsed.steps;
	const cat = pluralCategory(lang, count);
	const text = msg(`trace.summary_${cat}`, msg("trace.summary", null, { count }), { count });
	const labels = (Array.isArray(steps) ? steps : []).map(toStep).filter(Boolean);

	if (labels.length === 0) {
		return (
			<div className="trace-block trace-block--summary">
				<div className="trace-head">
					<span className="trace-icon"><BoltIcon /></span>
					<span className="trace-summary-text">{text}</span>
				</div>
			</div>
		);
	}

	return (
		<div className="trace-block trace-block--summary">
			<button
				type="button"
				className="trace-head trace-summary"
				aria-expanded={open}
				onClick={() => setOpen((v) => !v)}
			>
				<span className="trace-icon"><BoltIcon /></span>
				<span className="trace-summary-text">{text}</span>
				<span className="trace-sr-only">{` — ${msg(open ? "trace.hide" : "trace.show")}`}</span>
				<Chevron open={open} />
			</button>
			{open && (
				<div className="trace-body">
					<StepList steps={labels} msg={msg} />
				</div>
			)}
		</div>
	);
}
